import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { Activity, AlertTriangle, Download, RefreshCw, Shield, TrendingUp, Wifi, WifiOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/components/ui/use-toast';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

interface PercentileSummary {
  count: number;
  p50: number;
  p75: number;
  p95: number;
}

interface RuntimeSignalSummary {
  name: string;
  count: number;
  severity: 'info' | 'warn' | 'critical';
}

interface TelemetryAlertThresholds {
  errorRateWarnPct: number;
  errorRateCriticalPct: number;
  lcpP95WarnMs: number;
  lcpP95CriticalMs: number;
  fidP95WarnMs: number;
  fidP95CriticalMs: number;
  clsP95Warn: number;
  clsP95Critical: number;
  runtimeScoreWarn: number;
  runtimeScoreCritical: number;
}

interface TelemetryStats {
  totalRequests: number;
  acceptedEvents: number;
  droppedEvents: number;
  byLevel: {
    info: number;
    warn: number;
    error: number;
  };
  topEvents: Array<{ name: string; count: number }>;
  webVitals?: {
    lcpMs: PercentileSummary;
    fidMs: PercentileSummary;
    cls: PercentileSummary;
    longTaskMs: PercentileSummary;
  };
  runtimeSignals?: RuntimeSignalSummary[];
  updatedAt: number;
}

interface TelemetryHealthResponse {
  status: 'ok' | 'disabled';
  ingestEnabled?: boolean;
  thresholds?: TelemetryAlertThresholds;
  stats?: TelemetryStats;
}

interface TelemetryWindowResponse {
  status: 'ok' | 'disabled';
  ingestEnabled?: boolean;
  thresholds?: TelemetryAlertThresholds;
  generatedAt: number;
  window: {
    minutes: number;
    bucketSeconds: number;
    start: number;
    end: number;
    points: Array<{
      bucketStart: number;
      accepted: number;
      dropped: number;
      errors: number;
      warns: number;
      infos: number;
      errorRatePct: number;
      ingestRatePerMin: number;
    }>;
    totals: {
      accepted: number;
      dropped: number;
      errors: number;
      warns: number;
      infos: number;
      overallErrorRatePct: number;
    };
  };
  webVitals: {
    lcpMs: PercentileSummary;
    fidMs: PercentileSummary;
    cls: PercentileSummary;
    longTaskMs: PercentileSummary;
  };
  runtimeSignals: RuntimeSignalSummary[];
}

interface TimeseriesPoint {
  ts: number;
  label: string;
  errorRatePct: number;
  ingestRatePerMin: number;
  droppedRatePct: number;
}

interface RuntimeSignalRealtime {
  name: string;
  severity: 'info' | 'warn' | 'critical';
  totalCount: number;
  delta: number;
  realtimeScore: number;
}

type AlertLevel = 'normal' | 'warning' | 'critical';

type ThresholdKey = keyof TelemetryAlertThresholds;

interface AlertState {
  level: AlertLevel;
  reasons: string[];
}

const POLL_INTERVAL_MS = 5000;
const MAX_SERIES_POINTS = 72;
const ROLLING_WINDOW_MINUTES = 30;
const ROLLING_BUCKET_SECONDS = 10;
const ADMIN_THRESHOLDS_STORAGE_KEY = 'intouch-telemetry-admin-thresholds-v1';

function parseThresholdNumber(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function getClientEnvThresholdDefaults(): TelemetryAlertThresholds {
  return {
    errorRateWarnPct: parseThresholdNumber(import.meta.env.VITE_TELEMETRY_THRESHOLD_ERROR_RATE_WARN_PCT, 2),
    errorRateCriticalPct: parseThresholdNumber(import.meta.env.VITE_TELEMETRY_THRESHOLD_ERROR_RATE_CRITICAL_PCT, 5),
    lcpP95WarnMs: parseThresholdNumber(import.meta.env.VITE_TELEMETRY_THRESHOLD_LCP_P95_WARN_MS, 4000),
    lcpP95CriticalMs: parseThresholdNumber(import.meta.env.VITE_TELEMETRY_THRESHOLD_LCP_P95_CRITICAL_MS, 6000),
    fidP95WarnMs: parseThresholdNumber(import.meta.env.VITE_TELEMETRY_THRESHOLD_FID_P95_WARN_MS, 150),
    fidP95CriticalMs: parseThresholdNumber(import.meta.env.VITE_TELEMETRY_THRESHOLD_FID_P95_CRITICAL_MS, 300),
    clsP95Warn: parseThresholdNumber(import.meta.env.VITE_TELEMETRY_THRESHOLD_CLS_P95_WARN, 0.25),
    clsP95Critical: parseThresholdNumber(import.meta.env.VITE_TELEMETRY_THRESHOLD_CLS_P95_CRITICAL, 0.4),
    runtimeScoreWarn: parseThresholdNumber(import.meta.env.VITE_TELEMETRY_THRESHOLD_RUNTIME_SCORE_WARN, 16),
    runtimeScoreCritical: parseThresholdNumber(import.meta.env.VITE_TELEMETRY_THRESHOLD_RUNTIME_SCORE_CRITICAL, 32),
  };
}

function resolveTelemetryHealthUrl(): string {
  const configuredIngest = import.meta.env.VITE_TELEMETRY_ENDPOINT;
  if (!configuredIngest && import.meta.env.PROD) {
    return '';
  }

  const ingestUrl = configuredIngest || '/api/telemetry/ingest';
  if (ingestUrl.endsWith('/ingest')) {
    return ingestUrl.slice(0, -'/ingest'.length) + '/health';
  }
  return '/api/telemetry/health';
}

function resolveTelemetryWindowUrl(): string {
  const configuredIngest = import.meta.env.VITE_TELEMETRY_ENDPOINT;
  if (!configuredIngest && import.meta.env.PROD) {
    return '';
  }

  const ingestUrl = configuredIngest || '/api/telemetry/ingest';
  if (ingestUrl.endsWith('/ingest')) {
    return ingestUrl.slice(0, -'/ingest'.length) + '/window';
  }
  return '/api/telemetry/window';
}

function formatClock(ts: number): string {
  const date = new Date(ts);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function severityColor(severity: RuntimeSignalSummary['severity']): string {
  if (severity === 'critical') return 'text-red-600';
  if (severity === 'warn') return 'text-amber-600';
  return 'text-emerald-600';
}

function severityBadgeVariant(severity: RuntimeSignalSummary['severity']): 'destructive' | 'secondary' | 'outline' {
  if (severity === 'critical') return 'destructive';
  if (severity === 'warn') return 'secondary';
  return 'outline';
}

function anomalyScoreRealtime(signal: RuntimeSignalRealtime): number {
  return Number((signal.realtimeScore + signal.delta).toFixed(3));
}

function parseThresholds(value: unknown, fallback: TelemetryAlertThresholds): TelemetryAlertThresholds {
  if (!value || typeof value !== 'object') return fallback;
  const raw = value as Record<string, unknown>;

  return {
    errorRateWarnPct: parseThresholdNumber(raw.errorRateWarnPct, fallback.errorRateWarnPct),
    errorRateCriticalPct: parseThresholdNumber(raw.errorRateCriticalPct, fallback.errorRateCriticalPct),
    lcpP95WarnMs: parseThresholdNumber(raw.lcpP95WarnMs, fallback.lcpP95WarnMs),
    lcpP95CriticalMs: parseThresholdNumber(raw.lcpP95CriticalMs, fallback.lcpP95CriticalMs),
    fidP95WarnMs: parseThresholdNumber(raw.fidP95WarnMs, fallback.fidP95WarnMs),
    fidP95CriticalMs: parseThresholdNumber(raw.fidP95CriticalMs, fallback.fidP95CriticalMs),
    clsP95Warn: parseThresholdNumber(raw.clsP95Warn, fallback.clsP95Warn),
    clsP95Critical: parseThresholdNumber(raw.clsP95Critical, fallback.clsP95Critical),
    runtimeScoreWarn: parseThresholdNumber(raw.runtimeScoreWarn, fallback.runtimeScoreWarn),
    runtimeScoreCritical: parseThresholdNumber(raw.runtimeScoreCritical, fallback.runtimeScoreCritical),
  };
}

export function TelemetryDashboardPage() {
  const { userData } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(true);
  const [online, setOnline] = useState(navigator.onLine);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<TelemetryStats | null>(null);
  const [series, setSeries] = useState<TimeseriesPoint[]>([]);
  const [rollingWindow, setRollingWindow] = useState<TelemetryWindowResponse['window'] | null>(null);
  const [rollingVitals, setRollingVitals] = useState<TelemetryWindowResponse['webVitals'] | null>(null);
  const [runtimeRealtime, setRuntimeRealtime] = useState<Record<string, RuntimeSignalRealtime>>({});
  const [thresholds, setThresholds] = useState<TelemetryAlertThresholds>(() => getClientEnvThresholdDefaults());
  const [serverThresholds, setServerThresholds] = useState<TelemetryAlertThresholds | null>(null);
  const [hasAdminOverrides, setHasAdminOverrides] = useState(false);

  const telemetryHealthUrl = useMemo(() => resolveTelemetryHealthUrl(), []);
  const telemetryWindowUrl = useMemo(() => resolveTelemetryWindowUrl(), []);

  const isSuperAdmin = userData?.role === 'superadmin' || userData?.email === 'milan.krnjajic@gmail.com';
  const isAdmin = isSuperAdmin || !!userData?.isAdmin || userData?.role === 'admin';

  const previousRuntimeCountsRef = useRef<Record<string, number>>({});
  const lastAlertLevelRef = useRef<AlertLevel>('normal');
  const lastAlertToastAtRef = useRef(0);

  useEffect(() => {
    const envDefaults = getClientEnvThresholdDefaults();
    try {
      const raw = localStorage.getItem(ADMIN_THRESHOLDS_STORAGE_KEY);
      if (!raw) {
        setThresholds(envDefaults);
        return;
      }

      const parsed = JSON.parse(raw);
      const next = parseThresholds(parsed, envDefaults);
      setThresholds(next);
      setHasAdminOverrides(true);
    } catch {
      setThresholds(envDefaults);
      setHasAdminOverrides(false);
    }
  }, []);

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }

    if (!telemetryHealthUrl || !telemetryWindowUrl) {
      setLoading(false);
      setPolling(false);
      setError('Telemetry endpoint is not configured for this environment.');
      return;
    }

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const load = async () => {
      try {
        const [healthResponse, windowResponse] = await Promise.all([
          fetch(telemetryHealthUrl, {
            method: 'GET',
            headers: { Accept: 'application/json' },
          }),
          fetch(`${telemetryWindowUrl}?minutes=${ROLLING_WINDOW_MINUTES}&bucketSeconds=${ROLLING_BUCKET_SECONDS}`, {
            method: 'GET',
            headers: { Accept: 'application/json' },
          }),
        ]);

        if (!healthResponse.ok) {
          throw new Error(`Telemetry health request failed: ${healthResponse.status}`);
        }

        if (!windowResponse.ok) {
          throw new Error(`Telemetry window request failed: ${windowResponse.status}`);
        }

        const healthPayload = (await healthResponse.json()) as TelemetryHealthResponse;
        const windowPayload = (await windowResponse.json()) as TelemetryWindowResponse;

        if (cancelled) return;

        if (!healthPayload.stats) {
          setError('Telemetry health endpoint returned no stats.');
          setLoading(false);
          return;
        }

        if (!windowPayload.window) {
          setError('Telemetry window endpoint returned no window data.');
          setLoading(false);
          return;
        }

        setStats(healthPayload.stats);
        setRollingWindow(windowPayload.window);
        setRollingVitals(windowPayload.webVitals);
        setError(null);
        setLoading(false);

        const resolvedServerThresholds = parseThresholds(
          windowPayload.thresholds || healthPayload.thresholds,
          getClientEnvThresholdDefaults()
        );
        setServerThresholds(resolvedServerThresholds);
        if (!hasAdminOverrides) {
          setThresholds(resolvedServerThresholds);
        }

        const nextSeries = windowPayload.window.points
          .slice(-MAX_SERIES_POINTS)
          .map((point) => {
            const denominator = Math.max(1, point.accepted + point.dropped);
            return {
              ts: point.bucketStart,
              label: formatClock(point.bucketStart),
              errorRatePct: Number(((point.errors / denominator) * 100).toFixed(3)),
              ingestRatePerMin: Number(point.ingestRatePerMin.toFixed(3)),
              droppedRatePct: Number(((point.dropped / denominator) * 100).toFixed(3)),
            };
          });

        setSeries(nextSeries);

        const runtimeIncoming = windowPayload.runtimeSignals || [];
        setRuntimeRealtime((previous) => {
          const next: Record<string, RuntimeSignalRealtime> = { ...previous };
          const currentMap: Record<string, number> = {};

          for (const signal of runtimeIncoming) {
            currentMap[signal.name] = signal.count;
            const priorTotal = previousRuntimeCountsRef.current[signal.name] || 0;
            const delta = Math.max(0, signal.count - priorTotal);
            const priorScore = previous[signal.name]?.realtimeScore || 0;
            const severityWeight = signal.severity === 'critical' ? 3 : signal.severity === 'warn' ? 2 : 1;
            const realtimeScore = Number((priorScore * 0.72 + delta * severityWeight * 5).toFixed(3));

            next[signal.name] = {
              name: signal.name,
              severity: signal.severity,
              totalCount: signal.count,
              delta,
              realtimeScore,
            };
          }

          for (const key of Object.keys(next)) {
            if (!(key in currentMap)) {
              const faded = Number((next[key].realtimeScore * 0.55).toFixed(3));
              if (faded < 0.25) {
                delete next[key];
              } else {
                next[key] = {
                  ...next[key],
                  delta: 0,
                  realtimeScore: faded,
                };
              }
            }
          }

          previousRuntimeCountsRef.current = currentMap;
          return next;
        });
      } catch (fetchError: any) {
        if (cancelled) return;
        setError(fetchError?.message || 'Failed to load telemetry window.');
        setLoading(false);
      }
    };

    void load();

    if (polling) {
      intervalId = setInterval(() => {
        void load();
      }, POLL_INTERVAL_MS);
    }

    return () => {
      cancelled = true;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isAdmin, polling, telemetryHealthUrl, telemetryWindowUrl, hasAdminOverrides]);

  const updateThreshold = (key: ThresholdKey, rawValue: string) => {
    const numeric = Number(rawValue);
    if (!Number.isFinite(numeric)) return;

    setThresholds((previous) => ({
      ...previous,
      [key]: numeric,
    }));
  };

  const saveAdminThresholds = () => {
    try {
      localStorage.setItem(ADMIN_THRESHOLDS_STORAGE_KEY, JSON.stringify(thresholds));
      setHasAdminOverrides(true);
      toast({
        title: 'Telemetry thresholds saved',
        description: 'Admin threshold overrides are active for this operations console.',
      });
    } catch {
      toast({
        title: 'Save failed',
        description: 'Unable to persist admin thresholds in local storage.',
        variant: 'destructive',
      });
    }
  };

  const resetThresholdsToEnv = () => {
    const fallback = serverThresholds || getClientEnvThresholdDefaults();
    setThresholds(fallback);
    setHasAdminOverrides(false);
    try {
      localStorage.removeItem(ADMIN_THRESHOLDS_STORAGE_KEY);
    } catch {
      // Ignore storage errors.
    }

    toast({
      title: 'Thresholds reset',
      description: 'Using environment/server defaults for telemetry alerting.',
    });
  };

  const topRuntimeSignals = useMemo(() => {
    return Object.values(runtimeRealtime)
      .sort((a, b) => b.realtimeScore - a.realtimeScore)
      .slice(0, 8);
  }, [runtimeRealtime]);

  const overallErrorRate = useMemo(() => {
    if (rollingWindow) return rollingWindow.totals.overallErrorRatePct;
    if (!stats) return 0;
    const total = Math.max(1, stats.acceptedEvents + stats.droppedEvents);
    return Number(((stats.byLevel.error / total) * 100).toFixed(3));
  }, [rollingWindow, stats]);

  const currentAlertState = useMemo<AlertState>(() => {
    const reasons: string[] = [];
    let level: AlertLevel = 'normal';

    if (overallErrorRate >= thresholds.errorRateCriticalPct) {
      level = 'critical';
      reasons.push(`Error rate ${overallErrorRate}% exceeds critical threshold ${thresholds.errorRateCriticalPct}%`);
    } else if (overallErrorRate >= thresholds.errorRateWarnPct) {
      level = 'warning';
      reasons.push(`Error rate ${overallErrorRate}% exceeds warning threshold ${thresholds.errorRateWarnPct}%`);
    }

    const p95Lcp = rollingVitals?.lcpMs.p95 || 0;
    if (p95Lcp >= thresholds.lcpP95CriticalMs) {
      level = 'critical';
      reasons.push(`LCP P95 ${p95Lcp}ms exceeds critical threshold ${thresholds.lcpP95CriticalMs}ms`);
    } else if (p95Lcp >= thresholds.lcpP95WarnMs && level !== 'critical') {
      level = 'warning';
      reasons.push(`LCP P95 ${p95Lcp}ms exceeds warning threshold ${thresholds.lcpP95WarnMs}ms`);
    }

    const p95Fid = rollingVitals?.fidMs.p95 || 0;
    if (p95Fid >= thresholds.fidP95CriticalMs) {
      level = 'critical';
      reasons.push(`FID P95 ${p95Fid}ms exceeds critical threshold ${thresholds.fidP95CriticalMs}ms`);
    } else if (p95Fid >= thresholds.fidP95WarnMs && level !== 'critical') {
      level = 'warning';
      reasons.push(`FID P95 ${p95Fid}ms exceeds warning threshold ${thresholds.fidP95WarnMs}ms`);
    }

    const p95Cls = rollingVitals?.cls.p95 || 0;
    if (p95Cls >= thresholds.clsP95Critical) {
      level = 'critical';
      reasons.push(`CLS P95 ${p95Cls} exceeds critical threshold ${thresholds.clsP95Critical}`);
    } else if (p95Cls >= thresholds.clsP95Warn && level !== 'critical') {
      level = 'warning';
      reasons.push(`CLS P95 ${p95Cls} exceeds warning threshold ${thresholds.clsP95Warn}`);
    }

    const maxRuntimeScore = topRuntimeSignals.length > 0
      ? Math.max(...topRuntimeSignals.map((signal) => anomalyScoreRealtime(signal)))
      : 0;

    if (maxRuntimeScore >= thresholds.runtimeScoreCritical) {
      level = 'critical';
      reasons.push(`Runtime anomaly score ${maxRuntimeScore.toFixed(2)} exceeds critical threshold ${thresholds.runtimeScoreCritical}`);
    } else if (maxRuntimeScore >= thresholds.runtimeScoreWarn && level !== 'critical') {
      level = 'warning';
      reasons.push(`Runtime anomaly score ${maxRuntimeScore.toFixed(2)} exceeds warning threshold ${thresholds.runtimeScoreWarn}`);
    }

    return { level, reasons };
  }, [overallErrorRate, rollingVitals, topRuntimeSignals, thresholds]);

  useEffect(() => {
    const rank = (value: AlertLevel): number => {
      if (value === 'critical') return 2;
      if (value === 'warning') return 1;
      return 0;
    };

    const current = currentAlertState.level;
    const previous = lastAlertLevelRef.current;

    if (rank(current) > rank(previous)) {
      const now = Date.now();
      const cooldownMs = 45_000;
      if (now - lastAlertToastAtRef.current >= cooldownMs) {
        lastAlertToastAtRef.current = now;
        toast({
          title: current === 'critical' ? 'Critical Telemetry Alert' : 'Telemetry Warning Escalated',
          description: currentAlertState.reasons.slice(0, 2).join(' | ') || 'Threshold escalation detected.',
          variant: current === 'critical' ? 'destructive' : 'default',
        });
      }
    }

    lastAlertLevelRef.current = current;
  }, [currentAlertState, toast]);

  const alertBadge = useMemo(() => {
    if (currentAlertState.level === 'critical') {
      return <Badge variant="destructive">CRITICAL</Badge>;
    }
    if (currentAlertState.level === 'warning') {
      return <Badge variant="secondary">WARNING</Badge>;
    }
    return <Badge variant="outline">STABLE</Badge>;
  }, [currentAlertState.level]);

  const thresholdRows: Array<{ key: ThresholdKey; label: string; step?: number; min?: number }> = [
    { key: 'errorRateWarnPct', label: 'Error Rate Warn %', step: 0.1, min: 0 },
    { key: 'errorRateCriticalPct', label: 'Error Rate Critical %', step: 0.1, min: 0 },
    { key: 'lcpP95WarnMs', label: 'LCP P95 Warn (ms)', step: 1, min: 0 },
    { key: 'lcpP95CriticalMs', label: 'LCP P95 Critical (ms)', step: 1, min: 0 },
    { key: 'fidP95WarnMs', label: 'FID P95 Warn (ms)', step: 1, min: 0 },
    { key: 'fidP95CriticalMs', label: 'FID P95 Critical (ms)', step: 1, min: 0 },
    { key: 'clsP95Warn', label: 'CLS P95 Warn', step: 0.01, min: 0 },
    { key: 'clsP95Critical', label: 'CLS P95 Critical', step: 0.01, min: 0 },
    { key: 'runtimeScoreWarn', label: 'Runtime Score Warn', step: 0.1, min: 0 },
    { key: 'runtimeScoreCritical', label: 'Runtime Score Critical', step: 0.1, min: 0 },
  ];

  const errorRateLineData = useMemo(() => {
    return {
      labels: series.map((point) => point.label),
      datasets: [
        {
          label: 'Error Rate %',
          data: series.map((point) => point.errorRatePct),
          borderColor: 'rgba(220, 38, 38, 1)',
          backgroundColor: 'rgba(220, 38, 38, 0.18)',
          tension: 0.35,
          fill: true,
        },
        {
          label: 'Dropped Rate %',
          data: series.map((point) => point.droppedRatePct),
          borderColor: 'rgba(217, 119, 6, 1)',
          backgroundColor: 'rgba(217, 119, 6, 0.14)',
          tension: 0.35,
          fill: true,
        },
      ],
    };
  }, [series]);

  const ingestRateLineData = useMemo(() => {
    return {
      labels: series.map((point) => point.label),
      datasets: [
        {
          label: 'Ingest Rate / min',
          data: series.map((point) => point.ingestRatePerMin),
          borderColor: 'rgba(14, 165, 233, 1)',
          backgroundColor: 'rgba(14, 165, 233, 0.18)',
          tension: 0.3,
          fill: true,
        },
      ],
    };
  }, [series]);

  const webVitalsData = useMemo(() => {
    const vital = rollingVitals || stats?.webVitals;
    const labels = ['LCP (ms)', 'FID (ms)', 'CLS', 'Long Tasks (ms)'];
    return {
      labels,
      datasets: [
        {
          label: 'P50',
          data: [vital?.lcpMs.p50 || 0, vital?.fidMs.p50 || 0, vital?.cls.p50 || 0, vital?.longTaskMs.p50 || 0],
          backgroundColor: 'rgba(16, 185, 129, 0.72)',
        },
        {
          label: 'P75',
          data: [vital?.lcpMs.p75 || 0, vital?.fidMs.p75 || 0, vital?.cls.p75 || 0, vital?.longTaskMs.p75 || 0],
          backgroundColor: 'rgba(59, 130, 246, 0.72)',
        },
        {
          label: 'P95',
          data: [vital?.lcpMs.p95 || 0, vital?.fidMs.p95 || 0, vital?.cls.p95 || 0, vital?.longTaskMs.p95 || 0],
          backgroundColor: 'rgba(244, 63, 94, 0.72)',
        },
      ],
    };
  }, [rollingVitals, stats]);

  const runtimeSignalsData = useMemo(() => {
    return {
      labels: topRuntimeSignals.map((signal) => signal.name.replace('runtime.metric.', '')),
      datasets: [
        {
          label: 'Anomaly Score',
          data: topRuntimeSignals.map((signal) => anomalyScoreRealtime(signal)),
          backgroundColor: topRuntimeSignals.map((signal) => {
            if (signal.severity === 'critical') return 'rgba(220, 38, 38, 0.72)';
            if (signal.severity === 'warn') return 'rgba(217, 119, 6, 0.72)';
            return 'rgba(5, 150, 105, 0.72)';
          }),
          borderWidth: 0,
          borderRadius: 6,
        },
      ],
    };
  }, [topRuntimeSignals]);

  const commonLineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const exportJson = () => {
    const payload = {
      generatedAt: new Date().toISOString(),
      alertState: currentAlertState,
      thresholds,
      thresholdsSource: hasAdminOverrides ? 'admin_override' : 'env_or_server_default',
      serverThresholds,
      rollingWindow,
      rollingVitals,
      series,
      runtimeSignals: topRuntimeSignals,
      healthStats: stats,
    };

    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `telemetry-panels-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportCsv = () => {
    const rows: string[] = [];

    rows.push('section,key,value');
    rows.push(`thresholds,source,${hasAdminOverrides ? 'admin_override' : 'env_or_server_default'}`);
    rows.push(`thresholds,errorRateWarnPct,${thresholds.errorRateWarnPct}`);
    rows.push(`thresholds,errorRateCriticalPct,${thresholds.errorRateCriticalPct}`);
    rows.push(`thresholds,lcpP95WarnMs,${thresholds.lcpP95WarnMs}`);
    rows.push(`thresholds,lcpP95CriticalMs,${thresholds.lcpP95CriticalMs}`);
    rows.push(`thresholds,fidP95WarnMs,${thresholds.fidP95WarnMs}`);
    rows.push(`thresholds,fidP95CriticalMs,${thresholds.fidP95CriticalMs}`);
    rows.push(`thresholds,clsP95Warn,${thresholds.clsP95Warn}`);
    rows.push(`thresholds,clsP95Critical,${thresholds.clsP95Critical}`);
    rows.push(`thresholds,runtimeScoreWarn,${thresholds.runtimeScoreWarn}`);
    rows.push(`thresholds,runtimeScoreCritical,${thresholds.runtimeScoreCritical}`);

    rows.push('');
    rows.push('section,timestamp,label,errorRatePct,ingestRatePerMin,droppedRatePct');
    for (const point of series) {
      rows.push([
        'time_series',
        point.ts,
        point.label,
        point.errorRatePct,
        point.ingestRatePerMin,
        point.droppedRatePct,
      ].join(','));
    }

    rows.push('');
    rows.push('section,metric,count,p50,p75,p95');
    if (rollingVitals) {
      rows.push(['web_vitals', 'lcpMs', rollingVitals.lcpMs.count, rollingVitals.lcpMs.p50, rollingVitals.lcpMs.p75, rollingVitals.lcpMs.p95].join(','));
      rows.push(['web_vitals', 'fidMs', rollingVitals.fidMs.count, rollingVitals.fidMs.p50, rollingVitals.fidMs.p75, rollingVitals.fidMs.p95].join(','));
      rows.push(['web_vitals', 'cls', rollingVitals.cls.count, rollingVitals.cls.p50, rollingVitals.cls.p75, rollingVitals.cls.p95].join(','));
      rows.push(['web_vitals', 'longTaskMs', rollingVitals.longTaskMs.count, rollingVitals.longTaskMs.p50, rollingVitals.longTaskMs.p75, rollingVitals.longTaskMs.p95].join(','));
    }

    rows.push('');
    rows.push('section,name,severity,totalCount,delta,realtimeScore');
    for (const signal of topRuntimeSignals) {
      rows.push([
        'runtime_signals',
        `"${signal.name.replace(/"/g, '""')}"`,
        signal.severity,
        signal.totalCount,
        signal.delta,
        anomalyScoreRealtime(signal),
      ].join(','));
    }

    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `telemetry-panels-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!isAdmin) {
    return (
      <div className="dashboard-shell min-h-screen flex items-center justify-center px-4">
        <Card className="w-full max-w-xl border-amber-200 bg-amber-50/70">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-900">
              <Shield className="h-5 w-5" />
              Admin Access Required
            </CardTitle>
          </CardHeader>
          <CardContent className="text-amber-800">
            You do not have permission to access telemetry operations.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="dashboard-shell min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50/40 to-sky-50/35">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight text-slate-900">Telemetry Ops Center</h1>
              {alertBadge}
              <Badge variant={hasAdminOverrides ? 'secondary' : 'outline'}>
                {hasAdminOverrides ? 'Admin Thresholds' : 'Env Thresholds'}
              </Badge>
            </div>
            <p className="mt-1 text-sm text-slate-600">Real-time error rate, web-vitals percentiles, and runtime anomaly signals.</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={online ? 'outline' : 'destructive'} className="gap-1">
              {online ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
              {online ? 'Online' : 'Offline'}
            </Badge>
            <Button
              variant="outline"
              onClick={() => setPolling((value) => !value)}
              className="gap-2"
            >
              <Activity className="h-4 w-4" />
              {polling ? 'Pause Live' : 'Resume Live'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setRuntimeRealtime({});
                previousRuntimeCountsRef.current = {};
              }}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Reset Signals
            </Button>
            <Button variant="outline" onClick={exportCsv} className="gap-2">
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
            <Button variant="outline" onClick={exportJson} className="gap-2">
              <Download className="h-4 w-4" />
              Export JSON
            </Button>
          </div>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Alert Threshold Settings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-5">
              {thresholdRows.map((row) => (
                <div key={row.key} className="space-y-1">
                  <label className="text-xs font-medium text-slate-600">{row.label}</label>
                  <Input
                    type="number"
                    value={thresholds[row.key]}
                    min={row.min}
                    step={row.step || 1}
                    onChange={(event) => updateThreshold(row.key, event.target.value)}
                  />
                </div>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button onClick={saveAdminThresholds}>Save Admin Settings</Button>
              <Button variant="outline" onClick={resetThresholdsToEnv}>Reset To Env Defaults</Button>
              <span className="text-xs text-slate-500">These settings control escalation sensitivity without code changes.</span>
            </div>
          </CardContent>
        </Card>

        {currentAlertState.reasons.length > 0 && currentAlertState.level !== 'normal' && (
          <Card className="mb-6 border-amber-200 bg-amber-50/75">
            <CardContent className="py-4">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-800">
                <AlertTriangle className="h-4 w-4" />
                Threshold Escalation Active
              </div>
              <div className="space-y-1 text-xs text-amber-900">
                {currentAlertState.reasons.slice(0, 4).map((reason) => (
                  <div key={reason}>{reason}</div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {error && (
          <Card className="mb-6 border-red-200 bg-red-50/70">
            <CardContent className="flex items-center gap-2 py-4 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4" />
              {error}
            </CardContent>
          </Card>
        )}

        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Accepted Events</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-900">{(rollingWindow?.totals.accepted || stats?.acceptedEvents || 0).toLocaleString()}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Total Requests</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-900">{stats?.totalRequests?.toLocaleString() || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Window Error Rate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-rose-600">{overallErrorRate}%</div>
              <Progress value={Math.min(100, overallErrorRate * 5)} className="mt-2" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Live Runtime Signals</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-900">{topRuntimeSignals.length}</div>
              <p className="mt-1 text-xs text-slate-500">Top anomalies by real-time spike scoring</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <AlertTriangle className="h-4 w-4 text-rose-500" />
                Error-Rate Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <Line data={errorRateLineData} options={commonLineOptions} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <TrendingUp className="h-4 w-4 text-sky-500" />
                Ingest Throughput
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <Line data={ingestRateLineData} options={commonLineOptions} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Activity className="h-4 w-4 text-emerald-600" />
                Web Vitals Percentiles
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <Bar
                  data={webVitalsData}
                  options={{
                    ...commonLineOptions,
                    plugins: {
                      legend: { position: 'top' as const },
                    },
                  }}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                Top Runtime Anomaly Signals
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <Bar
                  data={runtimeSignalsData}
                  options={{
                    ...commonLineOptions,
                    indexAxis: 'y' as const,
                    plugins: {
                      legend: { display: false },
                    },
                  }}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Shield className="h-4 w-4 text-cyan-600" />
              Runtime Signal Detail
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-sm text-slate-500">Loading telemetry signal streams...</div>
            ) : topRuntimeSignals.length === 0 ? (
              <div className="py-8 text-sm text-slate-500">No runtime anomaly signals captured yet.</div>
            ) : (
              <div className="space-y-3">
                {topRuntimeSignals.map((signal) => (
                  <div key={signal.name} className="flex flex-col gap-2 rounded-xl border border-slate-200/80 bg-white p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900">{signal.name}</p>
                        <p className="text-xs text-slate-500">
                          Realtime score: {anomalyScoreRealtime(signal).toFixed(2)} · Delta: +{signal.delta} · Total: {signal.totalCount}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={severityBadgeVariant(signal.severity)}>{signal.severity}</Badge>
                        <span className={`text-sm font-semibold ${severityColor(signal.severity)}`}>{anomalyScoreRealtime(signal).toFixed(2)}</span>
                      </div>
                    </div>
                    <Progress
                      value={Math.min(100, (anomalyScoreRealtime(signal) / Math.max(1, anomalyScoreRealtime(topRuntimeSignals[0]))) * 100)}
                    />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
